import type { CollisionDetection, DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import {
    DndContext,
    DragOverlay,
    MouseSensor,
    TouchSensor,
    closestCenter,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { cn } from '@plato/ui/util/classes';
import { formatISO, intlFormat, parseISO } from 'date-fns';
import { groupBy } from 'lodash-es';
import { useMemo, useState, type ReactNode } from 'react';
import type { EventInput } from '../../../server/queries/utils/crm-event-build-input.ts';
import type { GetManyEventsListItem } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { getEventMetadata } from '../../../utils/event-utils.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { getInputFromCrmEventDetails } from '../utils/get-input-from-crm-event-details.ts';
import { getSortableEventId } from '../utils/get-sortable-event-id.ts';
import { moveRecurrenceWeekday } from '../utils/recurrence-rule.ts';
import { DaySuggestions } from './day-suggestions.tsx';
import type { DragScopeChoice } from './drag-recurrence-scope-dialog.tsx';
import { DragRecurrenceScopeDialog } from './drag-recurrence-scope-dialog.tsx';
import { EventsLoadingState } from './events-loading-state.tsx';
import { OverlayEventCard } from './overlay-event-card.tsx';
import { SortableEventCard } from './sortable-event-card.tsx';

export type Props = {
    dateFrom: string;
    dateTo: string;
    ownerId: string;
    days: string[];
};

type DraggableItemState = {
    id: string;
    targetDay: string;
};

function DroppableContainer({
    id,
    children,
    className,
}: {
    className?: string;
    id: string;
    children: React.ReactNode;
}): ReactNode {
    const { setNodeRef } = useDroppable({
        id,
    });

    return (
        <div
            className={cn('flex w-72 max-sm:w-full shrink-0 grow flex-col rounded-b-md pb-4 px-2', className)}
            ref={setNodeRef}
            data-testid={`day-column-${id}`}
        >
            {children}
        </div>
    );
}

function buildMovedSingleEventInput(event: GetManyEventsListItem, targetDay: string): EventInput {
    return {
        ...getInputFromCrmEventDetails(event),
        recurrenceRule: undefined,
        date: targetDay,
    };
}

function buildDraggedRecurringInput(
    event: GetManyEventsListItem,
    targetDay: string,
    choice: DragScopeChoice,
): EventInput {
    const prevData = getInputFromCrmEventDetails(event);

    if (choice === 'single') {
        return { ...prevData, date: targetDay, recurrenceRule: undefined };
    }

    // 'update-schedule' — the series follows the drag onto the new weekday, starting at the target
    // day. The drag handler decides whether to force the in-place rewrite path via occurrenceDate
    // (when the target is before the existing startDate).
    if (!event.recurrenceRule) {
        return { ...prevData, date: targetDay };
    }

    return {
        ...prevData,
        date: targetDay,
        recurrenceRule: moveRecurrenceWeekday(event.recurrenceRule, event.date, targetDay),
    };
}

export function EventsOverviewContentWeek({ dateFrom, dateTo, ownerId, days }: Props): ReactNode {
    const locale = useResolvedLocale();
    const apiUtils = api.useUtils();

    const today = formatISO(new Date(), { representation: 'date' });

    const params = { dateFrom, dateTo, ownerId };
    const { data: eventsOnServer, isLoading } = api.platoUser.query.events.getList.useQuery(params);

    const eventsOnServerByDays = useMemo<Record<string, GetManyEventsListItem[]>>(
        () => groupBy(eventsOnServer ?? [], (event) => event.date),
        [eventsOnServer],
    );

    const [draggableItemState, setDraggableItemState] = useState<DraggableItemState | null>(null);

    const draggedEvent = useMemo(
        () =>
            draggableItemState &&
            eventsOnServer?.find((event) => getSortableEventId(event) === draggableItemState.id),
        [draggableItemState, eventsOnServer],
    );

    // Takes into consideration dragged event to regroup events
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- legacy code
    const eventsByDays = useMemo(() => {
        const targetDay = draggableItemState?.targetDay;
        const eventsByDays: typeof eventsOnServerByDays = {
            ...eventsOnServerByDays,
        };

        if (draggedEvent && targetDay && targetDay !== draggedEvent.date) {
            const originalDay = draggedEvent.date;
            const targetDay = draggableItemState.targetDay;

            eventsByDays[originalDay] =
                eventsOnServerByDays[originalDay]?.filter(
                    (event) => event.event_id !== draggedEvent.event_id,
                ) ?? [];

            eventsByDays[targetDay] = [
                ...(eventsOnServerByDays[targetDay]?.filter(
                    (event) => event.event_id !== draggedEvent.event_id,
                ) ?? []),
                draggedEvent,
            ];
        }

        return eventsByDays;
    }, [eventsOnServerByDays, draggedEvent, draggableItemState?.targetDay]);

    const sendEvent = useAnalytics();

    const getStringId = (id: unknown): string | undefined => (typeof id === 'string' ? id : undefined);

    const updateEvent = api.platoUser.command.events.update.useMutation({
        onSuccess: (_, input) => {
            void apiUtils.platoUser.query.events.getEventById.reset({
                eventId: input.eventId,
            });
        },
    });

    const reorderEvents = api.platoUser.command.events.reorder.useMutation({
        onSettled: () => {
            void apiUtils.platoUser.query.events.getList.invalidate(params);
        },
    });

    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    );

    const closestNestedCenter: CollisionDetection = ({ droppableContainers, ...args }) => {
        // First, let's see if the `trash` droppable rect is intersecting
        const daysCollisions = closestCenter({
            ...args,
            droppableContainers: droppableContainers.filter(({ id }) => {
                const idString = getStringId(id);
                return idString ? days.includes(idString) : false;
            }),
        });

        const [firstDayCollision] = daysCollisions;
        const firstDayCollisionId = getStringId(firstDayCollision?.id);

        if (firstDayCollisionId) {
            return closestCenter({
                ...args,
                droppableContainers: droppableContainers.filter(({ id }) => {
                    const idString = getStringId(id);
                    return idString ? idString.includes(firstDayCollisionId) : false;
                }),
            });
        }

        return closestCenter({
            ...args,
            droppableContainers,
        });
    };

    const handleDragStart = (event: DragStartEvent): void => {
        const activeId = getStringId(event.active.id);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- legacy code
        const targetDay = getStringId(event.active.data.current?.sortable?.containerId);

        if (!activeId || !targetDay) {
            return;
        }

        setDraggableItemState({
            id: activeId,
            targetDay,
        });
    };

    const handleDragOver = (event: DragOverEvent): void => {
        const activeId = getStringId(event.active.id);
        const overId = getStringId(event.over?.id);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- legacy code
        const overContainerId = getStringId(event.over?.data.current?.sortable?.containerId);

        if (!activeId) {
            return;
        }

        const targetDay = overId && days.includes(overId) ? overId : overContainerId;

        if (targetDay && draggableItemState?.targetDay !== targetDay) {
            setDraggableItemState({
                id: activeId,
                targetDay,
            });
        }
    };

    const handleDragEnd = (event: DragEndEvent): void => {
        const { active, over } = event;

        const activeId = getStringId(active.id);
        const overId = getStringId(over?.id);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- legacy code
        const overContainerId = getStringId(over?.data.current?.sortable?.containerId);
        const targetDay = overId && days.includes(overId) ? overId : overContainerId;

        if (!activeId || !targetDay || !over) {
            setDraggableItemState(null);
            return;
        }

        if (activeId === (overId ?? targetDay)) {
            setDraggableItemState(null);
            return;
        }

        const activeEvent = eventsOnServer?.find((event) => getSortableEventId(event) === activeId);

        // Build the target column's event list as it should look at the moment of drop. The
        // `eventsByDays` closure already does this when handleDragOver has fired and state has
        // re-rendered — but a fast drop can land before that, leaving the dragged event missing
        // from `eventsByDays[targetDay]`. Always reconstruct from the server data + the dragged
        // event so the rest of the handler has a single, complete source of truth.
        const targetEventsDuringDrag: GetManyEventsListItem[] = [
            ...(eventsByDays[targetDay] ?? []).filter((event) => getSortableEventId(event) !== activeId),
            ...(activeEvent ? [activeEvent] : []),
        ];

        const sortedIds = targetEventsDuringDrag.map((event) => getSortableEventId(event));

        const oldIndex = sortedIds.indexOf(activeId);
        const newIndex = sortedIds.indexOf(overId ?? targetDay);
        const newOrder = arrayMove(sortedIds, oldIndex, newIndex);

        const isCrossDayMove = !!activeEvent && activeEvent.date !== targetDay;

        const newList = days.flatMap((day) =>
            day === targetDay
                ? newOrder
                      .map((id) => {
                          const event = targetEventsDuringDrag.find(
                              (event) => id === getSortableEventId(event),
                          );
                          // Pin the dragged event's date to the target so the cache reflects the
                          // visual placement. This keeps it under targetDay regardless of whether
                          // the target column already has events, until the user confirms (or the
                          // mutation completes) — or cancels, which invalidates back to server state.
                          if (event && isCrossDayMove && event.event_id === activeEvent.event_id) {
                              return { ...event, date: targetDay };
                          }
                          return event;
                      })
                      .filter((e): e is GetManyEventsListItem => !!e)
                : (eventsByDays[day] ?? []),
        );

        void apiUtils.platoUser.query.events.getList.cancel(params);
        apiUtils.platoUser.query.events.getList.setData(params, newList);

        if (activeEvent) {
            sendEvent('event_reordered', getEventMetadata(activeEvent));
        }

        // `newOrder` ids come from `sortedIds`, which used each event's *original* date. For a
        // cross-day move we then pin the dragged event's date to `targetDay` in `newList`, which
        // changes its `getSortableEventId(...)`. Looking the dragged row up in `newList` by
        // sortable id would therefore return `undefined` and ship `''` to the server, leaving
        // the dragged event with no per-day `eventPosition` — so it sorted to the end of the
        // column regardless of where the user dropped it. `targetEventsDuringDrag` still has
        // the un-pinned dragged event, so it's the right source for sortable-id → event_id resolution.
        const eventIdBySortableId = new Map(
            targetEventsDuringDrag.map((event) => [getSortableEventId(event), event.event_id]),
        );

        const saveNewOrderToDatabase = (): void => {
            reorderEvents.mutate({
                events_order: newOrder.map((eventId) => eventIdBySortableId.get(eventId) ?? ''),
                date: targetDay,
            });
        };

        if (isCrossDayMove) {
            const revertOptimistic = (): void => {
                void apiUtils.platoUser.query.events.getList.invalidate(params);
            };

            if (activeEvent.recurrenceRule) {
                void DialogStateManager.show(DragRecurrenceScopeDialog, {
                    onConfirm: (choice) => {
                        const data = buildDraggedRecurringInput(activeEvent, targetDay, choice);
                        const scope = choice === 'single' ? 'single' : 'following';
                        updateEvent.mutate(
                            {
                                eventId: activeEvent.event_id,
                                data,
                                scope,
                                occurrenceDate: activeEvent.date,
                            },
                            {
                                onSuccess: saveNewOrderToDatabase,
                                onError: revertOptimistic,
                            },
                        );
                    },
                    onCancel: revertOptimistic,
                });
            } else {
                updateEvent.mutate(
                    {
                        eventId: activeEvent.event_id,
                        data: buildMovedSingleEventInput(activeEvent, targetDay),
                    },
                    {
                        onSuccess: saveNewOrderToDatabase,
                        onError: revertOptimistic,
                    },
                );
            }
        } else {
            saveNewOrderToDatabase();
        }

        setDraggableItemState(null);
    };

    if (isLoading) {
        return <EventsLoadingState />;
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestNestedCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
        >
            <div className="w-full h-full overflow-x-auto">
                <div className="flex flex-row items-stretch min-h-full relative pb-4 gap-2">
                    {days.map((day) => {
                        const isToday = day === today;
                        const events = eventsByDays[day] ?? [];

                        const sortedIds = events.map((event) => getSortableEventId(event));

                        const date = parseISO(day);
                        const weekday = intlFormat(
                            date,
                            {
                                weekday: 'long',
                            },
                            { locale },
                        );
                        const dayNumber = intlFormat(
                            date,
                            {
                                day: 'numeric',
                            },
                            { locale },
                        );

                        return (
                            <DroppableContainer
                                key={day}
                                id={day}
                                className={isToday ? 'bg-blue-50' : 'bg-white'}
                            >
                                <div className={cn('sticky top-0 z-10 bg-white')}>
                                    <div
                                        className={cn('pb-3 pt-2 rounded-t-md', {
                                            'bg-blue-50': isToday,
                                        })}
                                    >
                                        {isToday ? (
                                            <Text color="neutral-950" weight="medium">
                                                {weekday}{' '}
                                                <Text color="blue-600" weight="medium">
                                                    {dayNumber}
                                                </Text>
                                            </Text>
                                        ) : (
                                            <Text>
                                                {weekday} {dayNumber}
                                            </Text>
                                        )}
                                    </div>
                                </div>
                                <SortableContext
                                    id={day}
                                    items={sortedIds}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className="space-y-3 grow">
                                        {events.map((event) => (
                                            <SortableEventCard
                                                key={event.event_id}
                                                event={event}
                                                date={event.date}
                                            />
                                        ))}
                                        {/* Suggestions are anchored on the day's visits; without any, there is no route to anchor on.
                                            Past days can no longer be planned. ISO date strings compare correctly as strings. */}
                                        {events.length > 0 && day >= today && (
                                            <>
                                                <div className="h-3" />
                                                <DaySuggestions day={day} ownerId={ownerId} />
                                            </>
                                        )}
                                    </div>
                                </SortableContext>
                            </DroppableContainer>
                        );
                    })}
                </div>
            </div>

            <DragOverlay>
                {draggedEvent ? <OverlayEventCard event={draggedEvent} date={draggedEvent.date} /> : null}
            </DragOverlay>
        </DndContext>
    );
}
